import { STATUS_PATH, WATCH_STATUS_PATH, readStatusBody, readWatchBody } from "odyssey-shared";
import { timeoutSignal, type TimeoutSignal } from "../timeout-signal.ts";
import { errorLine, requestable } from "./request-check.ts";
import { INITIAL_SERVER_VIEW, nextServerView, probeDelayMs, type Probe, type ServerView } from "./server-status.ts";

/**
 * The entry screens' probe of the gateway's `GET /status` (ADR-078 decisions 2 and 3): one request at a time, on the
 * waits `probeDelayMs` gives, none while the tab is hidden and one at once when it is shown again, and one at once
 * when the player asks. A request that fails outright (refused, reset, a tunnel's error that carries no CORS header)
 * or takes longer than `PROBE_TIMEOUT_MS` is unreachable; any answer at all is not. Only the request itself can be
 * unreachable: a probe the page could not build, or one that fails for a reason of the page's own, is logged and is
 * no news, so a mistake in the page never tells a player the server is offline. `probeStatus` is the one place the
 * page asks, and the source that outlives the gateway joins here: when the gateway gave no answer, and only then,
 * the probe asks the status watcher outside the site (ADR-078's amendment of 2026-10-13, P1.13-T3) for the
 * operator's notice. So the watcher is asked on the entry screens alone and on the gateway's own backoff, and the
 * gateway's answer, when there is one, is the only thing read.
 */

/**
 * How long one probe waits for its answer before it counts as unreachable: the account requests' deadline less
 * two seconds, so a probe never outlasts the next.
 */
export const PROBE_TIMEOUT_MS = 8_000;

/**
 * How long the poll waits on one probe before it gives it up and goes on: the probe's own time limit and a margin.
 * A probe ends by itself within `PROBE_TIMEOUT_MS`; this holds for the one that never settles (a request the browser
 * froze with the page and never ended, a time limit that did not fire), which would otherwise stop the poll for good
 * with the strip left saying whatever it last said.
 */
export const PROBE_GIVE_UP_MS = PROBE_TIMEOUT_MS + 2_000;

/**
 * How long after a probe started before Check now may start another. A refused connection settles in
 * milliseconds, so without it a held or hammered button asks as fast as it is pressed and spends the route's 60
 * requests a minute per address, which every player behind that address shares, in seconds. A press inside it is
 * not lost: the next probe is moved up to the cooldown's end. With it one page asks at most 30 times a minute.
 */
export const CHECK_NOW_COOLDOWN_MS = 2_000;

/**
 * How long the status watcher is waited for, once the gateway gave no answer. Short, so the gateway's time limit
 * and the watcher's together end inside `PROBE_GIVE_UP_MS`: a watcher that is slow costs the notice, never the
 * probe.
 */
export const WATCHER_TIMEOUT_MS = 1_500;

/**
 * Whether a configured watcher is an origin the page may ask: `http` or `https`, with nothing after the host.
 *
 * @param watcher The configured value.
 * @returns True for an origin.
 */
function isOrigin(watcher: string): boolean {
  try {
    const url = new URL(watcher);
    return (url.protocol === "https:" || url.protocol === "http:") && url.origin === watcher;
  } catch {
    return false;
  }
}

/**
 * Asks the status watcher for the operator's notice, once the gateway gave no answer.
 *
 * @param watcher The watcher's origin.
 * @param fetcher The page's `fetch`.
 * @param timeoutMs How long to wait.
 * @returns The notice, when the watcher answers a body the page reads that says the gateway is offline and holds
 *   one; null for anything else: no answer, an error, an odd body, no notice, or a watcher that says the gateway is
 *   online, since then the page's own way out is what failed and an old notice would mislead. Never rejects.
 */
async function watcherNotice(watcher: string, fetcher: typeof fetch, timeoutMs: number): Promise<string | null> {
  let limit: TimeoutSignal | null = null;
  try {
    limit = timeoutSignal(timeoutMs);
    const response = await fetcher(`${watcher}${WATCH_STATUS_PATH}`, { method: "GET", cache: "no-store", credentials: "omit", signal: limit.signal });
    if (response.status !== 200) {
      return null;
    }
    const body = readWatchBody(await response.json());
    return body !== null && body.state === "offline" ? body.notice : null;
  } catch {
    return null;
  } finally {
    limit?.settle();
  }
}

/**
 * Asks the gateway for its status once and, only when it gave no answer, the status watcher for the operator's
 * notice.
 *
 * @param base The gateway's HTTP origin.
 * @param fetcher The page's `fetch`.
 * @param timeoutMs How long to wait for the gateway's answer.
 * @param watcher The status watcher's origin as the page's configuration names it; empty for a page given none,
 *   which asks no one else, as is a value that is not an origin.
 * @param watcherTimeoutMs How long to wait for the watcher.
 * @returns What the probe found: unreachable only when the request was sent and got no answer, with the notice when
 *   the watcher gave one; never rejects.
 */
export async function probeStatus(base: string, fetcher: typeof fetch = fetch, timeoutMs: number = PROBE_TIMEOUT_MS, watcher: string = "", watcherTimeoutMs: number = WATCHER_TIMEOUT_MS): Promise<Probe> {
  const url = `${base}${STATUS_PATH}`;
  let limit: TimeoutSignal | null = null;
  let init: RequestInit;
  try {
    limit = timeoutSignal(timeoutMs);
    init = { method: "GET", cache: "no-store", credentials: "omit", signal: limit.signal };
    requestable(url, init);
  } catch (error) {
    limit?.settle();
    console.error("the status probe could not be built; it says nothing of the server", error);
    return { kind: "no-news" };
  }
  let probe: Probe;
  try {
    probe = await sentProbe(fetcher, url, init);
  } finally {
    limit.settle();
  }
  if (probe.kind !== "unreachable" || !isOrigin(watcher)) {
    return probe;
  }
  const notice = await watcherNotice(watcher, fetcher, watcherTimeoutMs);
  return notice === null ? probe : { kind: "unreachable", notice };
}

/**
 * Sends a built probe and reads its answer.
 *
 * @param fetcher The page's `fetch`.
 * @param url The gateway's status URL.
 * @param init The probe's options, its time limit among them.
 * @returns What the probe found; never rejects. A request that got no answer is logged as a warning, so a page that
 *   says offline leaves in its console why.
 */
async function sentProbe(fetcher: typeof fetch, url: string, init: RequestInit): Promise<Probe> {
  let response: Response;
  try {
    response = await fetcher(url, init);
  } catch (error) {
    console.warn(`the status probe got no answer: ${errorLine(error)}`);
    return { kind: "unreachable" };
  }
  if (response.status !== 200) {
    return { kind: "no-news" };
  }
  try {
    const body = readStatusBody(await response.json());
    return body === null ? { kind: "no-news" } : { kind: "answer", body };
  } catch {
    return { kind: "no-news" };
  }
}

/**
 * What the poll works through: the probe, the clock, a source of randomness for the jitter, a timer that returns
 * its cancel, the tab's visibility with a way to hear it change, and the timer the watch on each probe runs on.
 */
export interface StatusPollDeps {
  readonly probe: () => Promise<Probe>;
  readonly now?: () => number;
  readonly random?: () => number;
  readonly schedule?: (fn: () => void, ms: number) => () => void;
  readonly hidden?: () => boolean;
  readonly onShown?: (fn: () => void) => () => void;
  readonly watch?: (fn: () => void, ms: number) => () => void;
}

/**
 * Schedules on the page's timer.
 *
 * @param fn What to run.
 * @param ms How long to wait.
 * @returns Its cancel.
 */
function pageTimer(fn: () => void, ms: number): () => void {
  const timer = setTimeout(fn, ms);
  return () => clearTimeout(timer);
}

/**
 * Hears the tab being shown.
 *
 * @param fn What to run when it is.
 * @returns A function that stops listening.
 */
function onDocumentShown(fn: () => void): () => void {
  const listener = (): void => {
    if (document.visibilityState === "visible") {
      fn();
    }
  };
  document.addEventListener("visibilitychange", listener);
  return () => document.removeEventListener("visibilitychange", listener);
}

/**
 * The probe on its timer, and the view it keeps, which React reads through `subscribe` and `getSnapshot`. Nothing
 * can stop it but `stop`: a hidden tab keeps its timer and looks again when it fires, so a tab shown without the
 * browser saying so still probes; a probe that never settles is given up after `PROBE_GIVE_UP_MS`; and a probe that
 * fails in the page is logged. The last two say nothing of the server, so the view stays as it was.
 */
export class StatusPoll {
  private view: ServerView = INITIAL_SERVER_VIEW;
  private readonly listeners = new Set<() => void>();
  private cancel: (() => void) | null = null;
  private stopShown: (() => void) | null = null;
  private unwatch: (() => void) | null = null;
  private probeId = 0;
  private probedAt: number | null = null;
  private dueAt = 0;
  private inFlight = false;
  private waitingForShown = false;
  private running = false;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly schedule: (fn: () => void, ms: number) => () => void;
  private readonly hidden: () => boolean;
  private readonly watch: (fn: () => void, ms: number) => () => void;

  /**
   * @param deps The probe, the clock, the jitter, the timer and the tab's visibility.
   */
  public constructor(private readonly deps: StatusPollDeps) {
    this.now = deps.now ?? Date.now;
    this.random = deps.random ?? Math.random;
    this.schedule = deps.schedule ?? pageTimer;
    this.hidden = deps.hidden ?? (() => document.visibilityState === "hidden");
    this.watch = deps.watch ?? pageTimer;
  }

  /**
   * Probes at once and then on the waits the view asks for.
   */
  public start(): void {
    if (this.running) {
      return;
    }
    this.running = true;
    this.stopShown = (this.deps.onShown ?? onDocumentShown)(() => {
      if (this.waitingForShown) {
        this.cancel?.();
        this.cancel = null;
        this.run();
      }
    });
    this.run();
  }

  /**
   * Stops probing; the view stays as it was, and a probe still out is forgotten.
   */
  public stop(): void {
    this.running = false;
    this.inFlight = false;
    this.probeId += 1;
    this.cancel?.();
    this.cancel = null;
    this.unwatch?.();
    this.unwatch = null;
    this.stopShown?.();
    this.stopShown = null;
  }

  /**
   * Probes at once, as Check now asks, unless a probe is already out. Within `CHECK_NOW_COOLDOWN_MS` of the last
   * probe's start it asks nothing at once and moves the wait up to the cooldown's end, unless the wait ends sooner.
   */
  public checkNow(): void {
    if (!this.running || this.inFlight) {
      return;
    }
    const opensAt = this.probedAt === null ? this.now() : this.probedAt + CHECK_NOW_COOLDOWN_MS;
    if (this.now() < opensAt) {
      if (this.dueAt > opensAt) {
        this.cancel?.();
        this.waitFor(opensAt - this.now());
      }
      return;
    }
    this.cancel?.();
    this.cancel = null;
    this.run();
  }

  /**
   * The player acted after "The server is back.", which clears it.
   */
  public acknowledge(): void {
    this.set(nextServerView(this.view, { kind: "acknowledged" }, this.now()));
  }

  /**
   * Adds a listener for a changed view.
   *
   * @param listener Called after each change.
   * @returns A function that removes it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  /**
   * The view now.
   *
   * @returns The view.
   */
  public readonly getSnapshot = (): ServerView => this.view;

  /**
   * Probes once, unless the tab is hidden, which waits for it to be shown or for the timer to find it shown; then
   * waits for the next. A probe that rejects is the page's own failure and one that outlasts `PROBE_GIVE_UP_MS` is
   * given up: both are logged, leave the view as it was, and the poll goes on.
   */
  private run(): void {
    if (!this.running) {
      return;
    }
    if (this.hidden()) {
      this.waitingForShown = true;
      this.wait();
      return;
    }
    this.waitingForShown = false;
    this.inFlight = true;
    this.probedAt = this.now();
    this.probeId += 1;
    const id = this.probeId;
    this.unwatch = this.watch(() => {
      if (id === this.probeId && this.inFlight) {
        console.error(`the status probe did not end within ${PROBE_GIVE_UP_MS} ms and was given up; it says nothing of the server`);
        this.take(id, null);
      }
    }, PROBE_GIVE_UP_MS);
    void this.deps.probe().then(
      (probe) => this.take(id, probe),
      (error: unknown) => {
        console.error("the status probe failed in the page; it says nothing of the server", error);
        this.take(id, null);
      },
    );
  }

  /**
   * Ends a probe: takes what it found into the view, and waits for the next. A probe already ended (given up, or
   * followed by a newer one) is ignored.
   *
   * @param id The probe's number.
   * @param probe What it found, or null when it found nothing the view can use.
   */
  private take(id: number, probe: Probe | null): void {
    if (id !== this.probeId || !this.inFlight) {
      return;
    }
    this.inFlight = false;
    this.unwatch?.();
    this.unwatch = null;
    if (!this.running) {
      return;
    }
    if (probe !== null) {
      this.set(nextServerView(this.view, probe, this.now()));
    }
    this.wait();
  }

  /**
   * Waits the time the view asks for, then runs again.
   */
  private wait(): void {
    this.waitFor(probeDelayMs(this.view, this.random()));
  }

  /**
   * Waits a time, then runs again.
   *
   * @param ms How long.
   */
  private waitFor(ms: number): void {
    this.dueAt = this.now() + ms;
    this.cancel = this.schedule(() => {
      this.cancel = null;
      this.run();
    }, ms);
  }

  /**
   * Replaces the view and tells the listeners.
   *
   * @param view The new view.
   */
  private set(view: ServerView): void {
    this.view = view;
    this.notify();
  }

  /**
   * Tells every listener.
   */
  private notify(): void {
    for (const listener of this.listeners) {
      listener();
    }
  }
}
